import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import {
	TopNav,
	TopNavStart,
	TopNavMiddle,
	TopNavEnd,
	SideNavToggleButton,
} from '@atlaskit/navigation-system';

import type { GlobalTopNavigationProps, UseSideNavToggleStateResult } from '../../common/types';
import {
	useLocalToggleState,
	useNetworkToggleState,
} from '../../controllers/useSideNavToggleState';
import { AppHome } from '../app-home/AppHome';

import type {
	GlobalTopNavigation$data,
	GlobalTopNavigation$key,
} from './__generated__/GlobalTopNavigation.graphql';
import { messages } from './messages';

/**
 * The props shared by both store-specific wrappers and the presentational nav.
 */
type TopNavViewProps = Omit<GlobalTopNavigationProps, 'customizationStore' | 'queryRef'> & {
	/**
	 * The already-read `GlobalTopNavigation` fragment data, which carries the
	 * composed `useSideNavToggleStateQuery` spread. Only the network store reads
	 * it; the local store ignores it entirely.
	 */
	queryRef: GlobalTopNavigation$data;
};

export default function GlobalTopNavigation(props: GlobalTopNavigationProps): JSX.Element {
	const { queryRef, customizationStore = 'local', ...rest } = props;

	// Read the navigation customization data from the caller's query.
	// The caller is expected to spread `...GlobalTopNavigation` into a query.
	//
	// The actual `navPlatSidebarData` selection lives behind the composed
	// `...useSideNavToggleStateQuery` fragment (owned by the toggle-state
	// controller), and is skipped via `@skip(if: $skipNavPlatSidebarData)`.
	// Callers should set `skipNavPlatSidebarData` to `true` when the user is
	// anonymous (no personal customization to load) OR when `customizationStore`
	// is `'local'` (customization is read from `localStorage`, not the network).
	const data = useFragment<GlobalTopNavigation$key>(
		graphql`
			fragment GlobalTopNavigation on Query {
				...useSideNavToggleStateQuery
			}
		`,
		queryRef,
	);

	// Branch at the COMPONENT boundary, not inside a hook. Each wrapper calls
	// exactly one store hook, unconditionally — so the Rules of Hooks hold
	// statically (and the eslint hooks plugin can actually see it), and a change
	// to `customizationStore` correctly swaps stores instead of being silently
	// ignored.
	//
	// This mirrors how GSN picks between `<LocalStore>` and `<NetworkStore>`.
	// @see platform/packages/navigation/global-side-navigation/src/ui/GlobalSideNavigation.tsx
	// Both branches are module-level components, so this ternary is already
	// referentially stable — no `useMemo` required. Note that changing
	// `customizationStore` swaps the component type and therefore remounts the
	// subtree; see the prop's documentation.
	// Any network-backed mode (`'network'` or `'network-migration'`) uses the
	// network store; only `'local'` uses the device-local store. GTN does not yet
	// implement migration-specific behaviour, so `'network-migration'` currently
	// behaves like `'network'` here rather than silently falling back to local.
	const Store = customizationStore === 'local' ? LocalStoreTopNav : NetworkStoreTopNav;

	return <Store {...rest} queryRef={data} />;
}

/**
 * Local-storage variant. Calls `useLocalToggleState` unconditionally.
 */
function LocalStoreTopNav(props: TopNavViewProps): JSX.Element {
	const toggleState = useLocalToggleState(props.app);

	return <GlobalTopNavView {...props} {...toggleState} />;
}

/**
 * Network variant. Calls `useNetworkToggleState` unconditionally.
 */
function NetworkStoreTopNav(props: TopNavViewProps): JSX.Element {
	const toggleState = useNetworkToggleState(props.networkStorageParams, props.queryRef);

	return <GlobalTopNavView {...props} {...toggleState} />;
}

/**
 * Presentational top nav. Store-agnostic: it receives the already-resolved
 * collapsed state and writer from whichever store wrapper rendered it.
 */
function GlobalTopNavView(props: TopNavViewProps & UseSideNavToggleStateResult): JSX.Element {
	// NOTE: `app`, `networkStorageParams` and `queryRef` are intentionally
	// destructured but unused here — they are consumed by the store wrappers
	// above. Pulling them out keeps them OUT of `adsTopNavProps`, which is
	// spread onto ADS `<TopNav>`; leaving them in would forward unknown props to
	// the DOM. Do not "tidy up" these bindings.
	const {
		app,
		appLabel,
		AppHomeSlot,
		defaultCollapsed,
		setCollapsed,
		networkStorageParams,
		queryRef,
		UNSAFE__TopNavEndContent,
		UNSAFE__TopNavMiddleContent,
		UNSAFE__TopNavStartContent,
		...adsTopNavProps
	} = props;
	const { formatMessage } = useIntl();

	const handleSideNavToggle = useCallback<
		NonNullable<React.ComponentProps<typeof SideNavToggleButton>['onClick']>
	>(
		(_event, _analyticsEvent, attributes) => {
			// `isSideNavVisible` reflects the state *before* the toggle is applied.
			// So after the click, the side nav becomes collapsed when it was
			// previously visible (and vice versa).
			setCollapsed(Boolean(attributes?.isSideNavVisible));
		},
		[setCollapsed],
	);

	return (
		<TopNav {...adsTopNavProps}>
			<TopNavStart
				sideNavToggleButton={
					<SideNavToggleButton
						defaultCollapsed={defaultCollapsed}
						collapseLabel={formatMessage(messages.collapseSidebar)}
						expandLabel={formatMessage(messages.expandSidebar)}
						onClick={handleSideNavToggle}
					/>
				}
			>
				{AppHomeSlot ? <AppHomeSlot AppHomeComponent={AppHome} /> : <AppHome appName={appLabel} />}
				{UNSAFE__TopNavStartContent}
			</TopNavStart>
			<TopNavMiddle>{UNSAFE__TopNavMiddleContent}</TopNavMiddle>
			<TopNavEnd>{UNSAFE__TopNavEndContent}</TopNavEnd>
		</TopNav>
	);
}
